import { useState, useEffect } from 'react';
import { Card, Button, Table, message, Space, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import FormPreview from '../../components/FormBuilder/FormPreview';

interface Form {
  id: string;
  name: string;
  description?: string;
  layout: any;
  isTemplate: boolean;
  createdAt: string;
}

export default function FormsList() {
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewForm, setPreviewForm] = useState<Form | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/form-builder/forms?isTemplate=false', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setForms(data);
    } catch (error) {
      message.error('Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await fetch(`/api/v1/form-builder/forms/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      message.success('Form deleted successfully');
      fetchForms();
    } catch (error) {
      message.error('Failed to delete form');
    }
  };

  const handleClone = async (form: Form) => {
    try {
      const res = await fetch('/api/v1/form-builder/forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: `${form.name} (Copy)`,
          description: form.description,
          layout: form.layout,
          isTemplate: false,
        }),
      });
      
      if (res.ok) {
        message.success('Form cloned successfully');
        fetchForms();
      }
    } catch (error) {
      message.error('Failed to clone form');
    }
  };

  const columns = [
    {
      title: 'Form Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Form) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.description && (
            <div style={{ fontSize: 12, color: '#888' }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Layout',
      key: 'layout',
      render: (_: any, record: Form) => (
        <Tag color="blue">{record.layout.columns}-Column Grid</Tag>
      ),
    },
    {
      title: 'Fields',
      key: 'fields',
      render: (_: any, record: Form) => (
        <span>{record.layout.fields?.length || 0} fields</span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Form) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => setPreviewForm(record)}
          >
            Preview
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/feedback/forms/${record.id}/edit`)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => handleClone(record)}
          >
            Clone
          </Button>
          <Popconfirm
            title="Delete this form?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0 }}>Forms</h1>
          <p style={{ margin: '4px 0 0 0', color: '#888' }}>
            Create and manage custom feedback forms
          </p>
        </div>
        <Space>
          <Button onClick={() => navigate('/feedback/form-templates')}>
            Browse Templates
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/feedback/forms/new')}
          >
            Create Form
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={forms}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <FormPreview
        visible={!!previewForm}
        form={previewForm}
        onClose={() => setPreviewForm(null)}
      />
    </div>
  );
}
